import Box from "@mui/material/Box";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Order } from "src/types/order/order";
import { RoutePaths } from "src/constants";
import MaterialReactTable from "material-react-table";
import { MenuItem } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { AppDispatch } from "src/store";
import { fetchOrdersPagination } from "src/slices/orders/thunks";
import { selectOrders } from "src/slices/orders/selectors";
import { OrderStatusChip } from "src/components/pages/order/order-status-chip";
import { CustomerOrderedServicesGrid } from "src/components/pages/order/customer-ordered-services-grid";
import { dayMonthYearFormat } from "src/lib/utils";
import { selectTotalOrders } from "src/slices/dashboard/selectors";
import { fetchTotalOrders } from "src/slices/dashboard/thunks";

// ✅ local type definition for filters
type MRT_ColumnFiltersState = { id: string; value: any }[];

export const OrdersList = () => {
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(
    []
  );
  const [rows, setRows] = useState<any[]>([]);
  const [rowCount, setRowCount] = useState(0);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 100 });
  const [globalFilter, setGlobalFilter] = useState("");

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { state } = useLocation();

  const orders = useSelector(selectOrders);
  const totalOrders = useSelector(selectTotalOrders);

  const [columns] = useState<any>([
    { accessorKey: "orderId", header: "Order Id" },
    { accessorKey: "customerName", header: "Customer Name" },
    { accessorKey: "locationName", header: "Branch" },
    { accessorKey: "pickupDate", header: "Pickup Date" },
    { accessorKey: "dueDate", header: "Due Date" },
    {
      id: "orderStatus",
      accessorKey: "orderStatus",
      header: "Order Status",
      Cell: ({ cell }) => <OrderStatusChip orderStatus={cell.getValue()} />,
    },
    { accessorKey: "createdAt", header: "Created At" },
  ]);

  // ✅ Fetch total orders count once
  useEffect(() => {
    dispatch(fetchTotalOrders({}));
  }, [dispatch]);

  // ✅ Update total row count when totalOrders change
  useEffect(() => {
    setRowCount(
      totalOrders?.data?.reduce(
        (total, val) => total + parseInt(val.totalorders),
        0
      ) || 0
    );
  }, [totalOrders]);

  // ✅ Fetch paginated orders
  useEffect(() => {
    dispatch(
      fetchOrdersPagination({
        noOfRows: pagination.pageSize.toString(),
        pageNumber: (pagination.pageIndex + 1).toString(),
        customerName: globalFilter || "",
      })
    );
    // dispatch(fetchOrders());
    // eslint-disable-next-line
  }, [pagination.pageSize, pagination.pageIndex, globalFilter]);

  // ✅ Map orders to table rows
  useEffect(() => {
    const tableRows: any[] = orders.map((order: Order) => ({
      id: order.id,
      customerId: order.customerId,
      orderId: `LX-${order.id}`,
      customerName: order.customerName,
      pickupAddress: order.pickupAddress,
      deliveryAddress: order.deliveryAddress,
      createdAt: dayMonthYearFormat(new Date(order.orderDate), true),
      pickupDate: dayMonthYearFormat(new Date(order.pickUpDate)),
      dueDate: dayMonthYearFormat(new Date(order.dueDate)),
      orderStatus: order.orderStatus,
      servicesItems: order.servicesItems,
      invoiceNumber: order.invoicePlaneRefId,
      locationName: order.locationName,
      locationId: order.locationId,
    }));
    setRows(tableRows);
  }, [orders]);

  useEffect(() => {
    if (state && rows.length > 0) {
      const locationName =
        rows.find(
          (r) =>
            r.locationId?.toString() === state.defaultOrderBranch?.toString()
        )?.locationName || "";

      const filters = [{ id: "orderStatus", value: state.defaultOrderStatus }];
      if (locationName)
        filters.push({ id: "locationName", value: locationName });

      setColumnFilters(filters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, rows]);

  const isOverdue = useCallback((orderStatus: string, dueDate: string) => {
    if (!dueDate) return false;

    // Parse string "dd/MM/yyyy" back to Date
    const [day, month, year] = dueDate.split("/").map(Number);
    const due = new Date(year, month - 1, day); // month is 0-indexed

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    due.setHours(0, 0, 0, 0);

    return (
      (orderStatus?.toUpperCase() === "IN PROGRESS" ||
        orderStatus?.toUpperCase() === "READY FOR DELIVERY") &&
      due < today
    );
  }, []);

  return (
    <Box sx={{ width: 1 }}>
      <MaterialReactTable
        columns={columns}
        data={rows}
        manualPagination
        rowCount={rowCount}
        onPaginationChange={setPagination}
        onGlobalFilterChange={setGlobalFilter}
        onColumnFiltersChange={setColumnFilters} // ✅ FIXED: enables UI filters
        state={{
          pagination,
          columnFilters, // ✅ FIXED: pass filters to MRT state
        }}
        initialState={{
          density: "compact",
          pagination: {
            pageIndex: pagination.pageIndex,
            pageSize: pagination.pageSize,
          },
          showColumnFilters: !!state, // show filters only if navigated from dashboard
        }}
        enableRowActions
        renderDetailPanel={({ row }) => (
          <CustomerOrderedServicesGrid rows={row.original.servicesItems} />
        )}
        renderRowActionMenuItems={({ closeMenu, row }) => [
          <MenuItem
            key={0}
            onClick={() => {
              closeMenu();
              navigate(RoutePaths.UPDATE_ORDER, {
                state: { ...orders[row.index], isOrderUpdate: true },
              });
            }}
            sx={{ m: 0 }}
          >
            Order Update
          </MenuItem>,
          <MenuItem
            key={1}
            onClick={() => {
              closeMenu();
              navigate(`${RoutePaths.ORDER_DETAILS}/${row.original.id}`);
            }}
            sx={{ m: 0 }}
          >
            Order Details
          </MenuItem>,
        ]}
        muiTableBodyRowProps={({ row }) => {
          const overdue = isOverdue(
            row.original.orderStatus,
            row.original.dueDate
          );
          return {
            sx: {
              backgroundColor: overdue ? "#ffe6e6" : "inherit",
            },
            title: overdue
              ? `This order is overdue. Due date was ${row.original.dueDate}`
              : undefined,
          };
        }}
      />
    </Box>
  );
};
